/*
 * HOYA infinite scroll (snippets/hoya-infinite-scroll.liquid) for the collection, blog, search, collection list and
 * blog post comment pages, and for the header search drawer.
 *
 * The section marks each list that grows with data-hoya-infinite-list="<name>" and the selector of its items with
 * data-hoya-infinite-items. When the shopper nears the end of the page, the next page is fetched through the Section
 * Rendering API and its items are appended to the list of the same name. Coming back to the page (back button or
 * reload) rebuilds the pages that were loaded and scrolls back to the item the shopper was looking at.
 *
 * In the search drawer ("in-scroll-area") the list scrolls inside the drawer: the loader watches the drawer instead of
 * the page and restores nothing. A loader with "lead" waits until assets/hoya-search-ranking.js has put the products
 * whose title holds every word of the query first. That script can also queue "then-urls": the pages to load once the
 * current ones end, where "#held" stands for the items it set aside in a template until the title matches are loaded.
 */
(() => {
  if (!('IntersectionObserver' in window) || window.customElements.get('hoya-infinite-scroll')) {
    return; // The regular pagination stays visible.
  }

  const STORAGE_KEY = 'hoya-infinite-scroll';
  const RESTORE_MAX_AGE = 30 * 60 * 1000;
  const RESTORE_MAX_PAGES = 10;
  const REMEMBERED_URLS = 10;
  const PRELOAD_MARGIN = '0px 0px 200% 0px'; // Two screens ahead of the end of the list.
  const TAB_SWITCH_TIME = 1000; // The theme animates a tab switch for about 0.7 s.
  const LEAD_WAIT = 6000; // The longest wait for the title matches before the next pages load anyway.
  const FETCH_TIMEOUT = 15000;
  const LIST_SELECTOR = '[data-hoya-infinite-list]';
  const HELD = '#held';

  let restoreChecked = false;

  const currentUrl = () => location.pathname + location.search;

  const readPositions = () => {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
    } catch (error) {
      return {};
    }
  };

  const writePosition = (url, position) => {
    try {
      const positions = readPositions();
      delete positions[url];

      if (position) {
        positions[url] = position;
      }

      const urls = Object.keys(positions);
      urls.slice(0, Math.max(0, urls.length - REMEMBERED_URLS)).forEach((oldUrl) => delete positions[oldUrl]);
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
    } catch (error) {
      // Storage can be full or blocked; the scroll position is then simply not restored.
    }
  };

  const navigationType = () => performance.getEntriesByType?.('navigation')[0]?.type;

  const isItem = (element) => element.matches(element.parentElement?.dataset.hoyaInfiniteItems || '*');

  const itemsOf = (list) => Array.from(list.children).filter(isItem);

  // Product cards carry their handle; the other items are told apart by the page they link to.
  const keyOf = (item) => {
    const link = item.matches('a[href]') ? item : item.querySelector('a[href]');
    return item.dataset.hoyaInfiniteKey || item.getAttribute('handle') || link?.getAttribute('href') || null;
  };

  const isShown = (element) => element.getClientRects().length > 0;

  // The drawer that scrolls around the list, through the shadow roots of the drawer and of its slots.
  const scrollAreaOf = (element) => {
    for (let node = element; node;) {
      node = node.assignedSlot || node.parentElement || node.getRootNode().host || null;

      if (node && ['auto', 'scroll', 'overlay'].includes(getComputedStyle(node).overflowY)) {
        return node;
      }
    }

    return null;
  };

  class HoyaInfiniteScroll extends HTMLElement {
    static get observedAttributes() {
      return ['lead'];
    }

    connectedCallback() {
      if (this.hasAttribute('lead')) {
        // The title matches come first: assets/hoya-search-ranking.js removes "lead" once they are in place.
        this.leadTimer = setTimeout(() => this.removeAttribute('lead'), LEAD_WAIT);
      } else {
        this.start();
      }
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue !== null && newValue === null && this.isConnected && !this.abortController) {
        this.start();
      }
    }

    disconnectedCallback() {
      clearTimeout(this.leadTimer);
      this.abortController?.abort();
      this.abortController = null;
      this.observer?.disconnect();
    }

    start() {
      clearTimeout(this.leadTimer);
      this.inScrollArea = this.hasAttribute('in-scroll-area');

      // Only the lists of the page load can be rebuilt; lists rendered later by the filters start fresh. The search
      // drawer is never rebuilt, so it leaves that chance to the page.
      const mayRestore = !this.inScrollArea && !restoreChecked;
      restoreChecked = restoreChecked || !this.inScrollArea;

      this.root = this.closest('.shopify-section') || document.body;
      this.thenUrls = this.readThenUrls();
      this.nextUrl = this.getAttribute('next-url') || this.thenUrls.shift() || null;
      this.startPage = Number(this.getAttribute('page')) || 1;
      this.steps = [];
      this.signature = JSON.stringify([this.startPage, this.nextUrl, this.thenUrls]);

      if (!this.nextUrl || this.lists().length === 0) {
        return; // Last page: nothing to load, the page links stay visible.
      }

      this.abortController = new AbortController();
      const signal = this.abortController.signal;
      const scrollArea = this.inScrollArea ? scrollAreaOf(this) : null;

      this.toggleAttribute('active', true);
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadNext();
        }
      }, { root: scrollArea, rootMargin: PRELOAD_MARGIN });

      // A jump to the bottom of the page (End key, fast fling) can carry the end of the list from below the observed
      // area to above the screen in one step, which the observer never reports: a scroll that finds the end of the list
      // above the screen loads one page. The check is armed again only once the end of the list is back on or below
      // the screen, so the scroll corrections that follow an append (which keep it above) cannot load page after page.
      // In the drawer, whose last row is the "view all results" button, a scroll that brings the end of the list
      // within the observed area loads the next page.
      this.jumpCheckArmed = true;
      (scrollArea || window).addEventListener('scroll', () => {
        if (this.scrollCheckQueued) {
          return;
        }

        this.scrollCheckQueued = true;
        requestAnimationFrame(() => {
          this.scrollCheckQueued = false;

          if (!isShown(this)) {
            return;
          }

          if (scrollArea) {
            const area = scrollArea.getBoundingClientRect();

            if (this.getBoundingClientRect().top < area.bottom + 2 * area.height && !this.paused) {
              this.loadNext();
            }
          } else if (this.getBoundingClientRect().top >= 0) {
            this.jumpCheckArmed = true;
          } else if (this.jumpCheckArmed && !this.loading && !this.paused) {
            this.jumpCheckArmed = false;
            this.loadNext();
          }
        });
      }, { passive: true, signal });

      this.querySelector('.hoya-infinite-scroll__error button')?.addEventListener('click', () => {
        this.paused = false;
        this.loadNext();
      }, { signal });

      if (!this.inScrollArea) {
        this.root.addEventListener('click', (event) => {
          const item = event.target.closest(`${LIST_SELECTOR} > *`);
          this.clickedItem = item && isItem(item) ? { item, at: Date.now() } : null;
        }, { capture: true, signal });
        window.addEventListener('pagehide', () => this.rememberPosition(), { signal });
      }

      const position = mayRestore ? this.restorablePosition() : null;

      if (position) {
        this.restore(position);
      } else {
        this.observer.observe(this);
      }
    }

    readThenUrls() {
      try {
        const urls = JSON.parse(this.getAttribute('then-urls') || '[]');
        return Array.isArray(urls) ? urls.filter((url) => typeof url === 'string' && url) : [];
      } catch (error) {
        return [];
      }
    }

    lists() {
      return Array.from(this.root.querySelectorAll(LIST_SELECTOR));
    }

    items() {
      return this.lists().flatMap(itemsOf);
    }

    async loadNext() {
      if (this.loading || !this.nextUrl || !this.abortController) {
        return;
      }

      this.loading = true;
      this.setStatus('loading');

      try {
        const step = this.nextUrl;
        const page = step === HELD ? this.heldPage() : await this.fetchPage(step);

        if (!this.isConnected) {
          return;
        }

        const added = this.append(page.lists);
        this.steps.push(step);
        this.advance(page.nextUrl);
        this.loading = false;
        this.announce();
        this.setStatus(this.nextUrl ? 'idle' : 'done');
        this.watch(added.some(isShown));
      } catch (error) {
        this.loading = false;

        if (error.name !== 'AbortError') {
          this.paused = true; // No automatic retry loop: the shopper retries with the button.
          this.observer.unobserve(this);
          this.setStatus('error');
        }
      }
    }

    advance(nextUrl) {
      this.nextUrl = nextUrl || this.thenUrls.shift() || null;
    }

    async fetchPage(url) {
      const requestUrl = new URL(url, location.origin);
      requestUrl.searchParams.set('section_id', this.getAttribute('section-id'));

      // A request that never answers (seen once on a stalled connection) ends in the retry row, not an endless spinner.
      const signal = this.abortController.signal;
      const request = new AbortController();
      const stop = () => request.abort();
      const timer = setTimeout(stop, FETCH_TIMEOUT);
      signal.addEventListener('abort', stop, { once: true });
      let text;

      try {
        const response = await fetch(requestUrl, { signal: request.signal });

        if (!response.ok) {
          throw new Error(`Next page request failed with ${response.status}`);
        }

        text = await response.text();
      } catch (error) {
        throw error.name === 'AbortError' && !signal.aborted ? new Error('Next page request timed out') : error;
      } finally {
        clearTimeout(timer);
        signal.removeEventListener('abort', stop);
      }

      const html = new DOMParser().parseFromString(text, 'text/html');
      const lists = Array.from(html.querySelectorAll(LIST_SELECTOR));

      if (lists.length === 0) {
        throw new Error('Next page response has no list');
      }

      return {
        lists,
        nextUrl: html.querySelector('hoya-infinite-scroll')?.getAttribute('next-url') || null
      };
    }

    heldPage() {
      // The items assets/hoya-search-ranking.js set aside while the title matches were loading.
      const templates = Array.from(this.root.querySelectorAll('template[data-hoya-infinite-held]'));

      return {
        lists: templates.map((template) => {
          const list = document.createElement('div');
          list.dataset.hoyaInfiniteList = template.dataset.hoyaInfiniteHeld;
          list.append(template.content);
          template.remove();
          return list;
        }),
        nextUrl: null
      };
    }

    append(fetchedLists) {
      return fetchedLists.flatMap((fetchedList) => {
        const name = fetchedList.dataset.hoyaInfiniteList;
        const list = this.lists().find((candidate) => candidate.dataset.hoyaInfiniteList === name) || this.addTab(fetchedList);

        if (!list) {
          return [];
        }

        const keys = new Set(itemsOf(list).map(keyOf));
        const added = [];

        itemsOf(fetchedList).forEach((item) => {
          const key = keyOf(item);

          if (key && keys.has(key)) {
            return; // The order moved between two requests.
          }

          keys.add(key);
          const importedItem = document.importNode(item, true);
          importedItem.querySelectorAll('img:not([loading])').forEach((image) => image.setAttribute('loading', 'lazy'));
          added.push(importedItem);
        });

        list.append(...added);
        this.reveal(list, added);
        return added;
      });
    }

    addTab(fetchedList) {
      // Search: a kind of result (products, blog posts, pages) that first shows up on a later page gets its own tab.
      const tabs = this.root.querySelector('x-tabs');
      const panel = fetchedList.closest('[slot="content"]');
      const button = panel?.previousElementSibling;

      if (!tabs?.shadowRoot || !button?.matches('[slot="title"]')) {
        return null;
      }

      const newButton = document.importNode(button, true);
      const newPanel = document.importNode(panel, true);
      const list = Array.from(newPanel.querySelectorAll(LIST_SELECTOR)).find((candidate) => candidate.dataset.hoyaInfiniteList === fetchedList.dataset.hoyaInfiniteList);

      itemsOf(list).forEach((item) => item.remove()); // The items are appended like those of any other list.
      tabs.append(newButton, newPanel);

      if (!tabs.shadowRoot.querySelector('slot[name="title"]')) {
        // The first page had a single kind of result, so it was rendered without the tab list.
        const tabList = document.createElement('div');
        tabList.className = 'scrollable';
        tabList.setAttribute('part', 'tab-list-scrollable');
        tabList.innerHTML = '<slot role="tablist" part="tab-list" name="title"></slot>';
        tabs.shadowRoot.querySelector('slot[name="content"]')?.before(tabList);
      }

      tabs.querySelectorAll(':scope > [slot="title"]').forEach((tabButton, index) => {
        tabButton.setAttribute('aria-selected', String(index === (tabs.selectedIndex ?? 0)));
      });

      return list;
    }

    reveal(list, items) {
      // The theme reveals the first page once; items added later get the same entrance.
      const allowMotion = window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
      const listReveals = list.getAttribute('reveal-on-scroll') === 'true';

      items.forEach((item, index) => {
        const reveals = listReveals || item.getAttribute('reveal-on-scroll') === 'true';
        item.removeAttribute('reveal-on-scroll');

        if (reveals && allowMotion && item.animate) {
          item.animate(
            [{ opacity: 0, transform: 'translateY(20px)' }, { opacity: 1, transform: 'translateY(0)' }],
            { duration: 200, delay: Math.min(index, 12) * 50, easing: 'ease-in-out', fill: 'backwards' }
          );
        }
      });
    }

    watch(addedVisible = true) {
      if (!this.nextUrl) {
        this.observer.disconnect();
      } else if (!addedVisible && isShown(this)) {
        // Nothing new on screen (search: the page held results of another tab), so the next page is needed anyway.
        this.loadNext();
      } else {
        // Observing again reports the current state, so a list that is still too short keeps loading.
        this.observer.unobserve(this);
        this.observer.observe(this);
      }
    }

    setStatus(status) {
      const error = this.querySelector('.hoya-infinite-scroll__error');

      this.querySelector('.hoya-infinite-scroll__loading').hidden = status !== 'loading';
      error.hidden = status !== 'error';
      this.lists().forEach((list) => list.setAttribute('aria-busy', String(status === 'loading')));

      if (status === 'error') {
        this.querySelector('[role="status"]').textContent = error.querySelector('p').textContent;
      }

      if (status === 'done') {
        if (this.startPage > 1) {
          this.removeAttribute('active'); // Opened mid-list: the page links lead back to the earlier pages.
        } else if (this.steps.length > 0) {
          const end = this.querySelector('.hoya-infinite-scroll__end');
          end.textContent = (this.getAttribute('all-loaded-message') || '').replace('[shown]', this.items().length);
          end.hidden = false;
        }
      }
    }

    announce() {
      const status = this.querySelector('[role="status"]');
      status.textContent = (this.getAttribute('loaded-message') || '').replace('[shown]', this.items().length);
    }

    tabs() {
      const tabs = this.root.querySelector('x-tabs');
      return tabs?.matches(':defined') ? tabs : null;
    }

    async selectTab(index) {
      // Search: back on the tab the shopper was on.
      const tabs = this.tabs();

      if (!tabs || typeof index !== 'number' || tabs.selectedIndex === index) {
        return;
      }

      tabs.selectedIndex = index;
      await new Promise((resolve) => setTimeout(resolve, TAB_SWITCH_TIME));
    }

    rememberPosition() {
      // A list that assets/hoya-search-ranking.js reordered is rebuilt even when no page was appended.
      if (this.steps.length === 0 && !this.hasAttribute('composed')) {
        writePosition(currentUrl(), null); // Nothing was appended: the browser restores the scroll position on its own.
        return;
      }

      const clicked = this.clickedItem && Date.now() - this.clickedItem.at < 5000 && this.clickedItem.item.isConnected && isShown(this.clickedItem.item) ? this.clickedItem.item : null;
      const item = clicked || this.firstVisibleItem();

      writePosition(currentUrl(), {
        signature: this.signature,
        steps: this.steps,
        key: item ? keyOf(item) : null,
        top: item ? item.getBoundingClientRect().top : 0,
        tab: this.tabs()?.selectedIndex ?? null,
        scrollY: window.scrollY,
        savedAt: Date.now()
      });
    }

    firstVisibleItem() {
      return this.items().find((item) => isShown(item) && item.getBoundingClientRect().bottom > 0) || null;
    }

    restorablePosition() {
      if (window.Shopify?.designMode || !['back_forward', 'reload'].includes(navigationType())) {
        return null;
      }

      const position = readPositions()[currentUrl()];

      if (!position || position.signature !== this.signature || !Array.isArray(position.steps) || Date.now() - position.savedAt > RESTORE_MAX_AGE) {
        return null;
      }

      return position.steps.length > 0 || this.hasAttribute('composed') ? position : null;
    }

    async restore(position) {
      let shopperScrolled = false;
      const userInput = new AbortController();
      const onInput = () => {
        shopperScrolled = true;
        userInput.abort();
      };

      ['wheel', 'touchstart', 'keydown'].forEach((type) => window.addEventListener(type, onInput, { passive: true, signal: userInput.signal }));

      this.loading = true;
      this.setStatus('loading');

      try {
        const steps = position.steps.slice(0, RESTORE_MAX_PAGES);
        const pages = await Promise.all(steps.map((step) => (step === HELD ? null : this.fetchPage(step))));

        // The steps are replayed in their order, so the pages after them come from the same queue as before.
        for (const [index, step] of steps.entries()) {
          if (step !== this.nextUrl) {
            break; // The pages changed in the meantime: carry on from here.
          }

          const page = pages[index] || this.heldPage();
          this.append(page.lists);
          this.steps.push(step);
          this.advance(page.nextUrl);
        }

        this.announce();

        if (!shopperScrolled) {
          await this.selectTab(position.tab);
        }

        const key = position.key ?? position.handle;
        const item = key ? this.items().find((candidate) => keyOf(candidate) === key && isShown(candidate)) : null;

        // Images and fonts that settle after the rebuild can still move the item: hold it in place for a moment.
        for (const delay of [0, 150, 400, 800]) {
          await new Promise((resolve) => setTimeout(resolve, delay));

          if (shopperScrolled) {
            break;
          }

          window.scrollTo(0, item?.isConnected ? item.getBoundingClientRect().top + window.scrollY - position.top : position.scrollY);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }
        // The pages could not be rebuilt: carry on from the first page as on a regular visit.
      } finally {
        userInput.abort();
        this.loading = false;
      }

      if (!this.isConnected) {
        return;
      }

      this.setStatus(this.nextUrl ? 'idle' : 'done');
      this.watch();
    }
  }

  window.customElements.define('hoya-infinite-scroll', HoyaInfiniteScroll);
})();
